import { call, delay, select, takeLatest } from 'redux-saga/effects';
import { Rpc } from '@dynamika/rpc-core/contracts';
import { segmentationSlice } from './segmentation.slice';
import {
  scoringFormSegmentationChangedAction,
  scoringFormSelectedLabelsAction,
  scoringFormViewerBusyChangedAction,
} from 'src/pages/Readings/ReadingTaskPage/utils';
import { selectStoreDataRelatedToMeasurements } from '@restore/measurements/measurements.selectors';
import { findScoringCode } from '@restore/measurements/measurements.saga';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  resolveFormCall,
  rejectFormCall,
} from 'src/services/rpc/pendingCalls';
import * as api from '@api/readerMeasurements';
import { createKeepWatcherAlive } from '../keepWatcherAlive';
import {
  Segmentation,
  SegmentationMeasurementReqParams,
  SegmentationDeleteParams,
  SegmentationServiceLockRequestParams,
  SegmentationUnlockParams,
  ToSegmPayload,
  VolumeStats,
} from '@restore/segmentation/segmentation.type';
import { MeasurementReqParams } from '@dynamika-shared/types/measurements';
import { produce, Draft } from 'immer';
import { ScoringFormsEnum } from '@dynamika-shared/types/scoringForms';

import { clinicalTrialInventorySlice } from '../clinicalTrialInventory/clinicalTrialInventory.slice';
import { ClinicalTrialInventoryType } from '@dynamika-shared/types/clinicalTrialInventory';

const MM3_UNITS = ['mm^3', 'mm³', 'mm3'];

// See D7-7639 / D7-7995: shared with `measurements.saga.ts` so the two sagas
// can't drift out of sync on this safety net again.
const keepWatcherAlive = createKeepWatcherAlive('segmentationSaga');

function* selectMeasurementsContextIfAvailable() {
  const rootState = (yield select()) as Record<string, unknown>;
  const hasReaderContext =
    rootState.measurements && rootState.readerTask && rootState.readerSubTask;

  if (!hasReaderContext) {
    return null;
  }

  return (yield select(
    selectStoreDataRelatedToMeasurements
  )) as ReturnType<typeof selectStoreDataRelatedToMeasurements>;
}

function normalizeVolumeInPayloadToMl(
  payloadData: Segmentation,
  shouldConvertVolumeToMl = true
) {
  if (!shouldConvertVolumeToMl || !payloadData?.segments) return payloadData;
  return produce(payloadData, (draft: Draft<Segmentation>) => {
    Object.keys(draft.segments || {}).forEach(key => {
      const s = draft.segments[Number(key)];
      const volume = s?.cachedStats?.namedStats?.volume;
      if (!volume) return;
      const unit = String(volume.unit || '');
      if (MM3_UNITS.includes(unit)) {
        const valueML = Number(volume.value) / 1000;
        // Ensure nested structures exist
        if (!s.cachedStats) s.cachedStats = {};
        if (!s.cachedStats.namedStats) s.cachedStats.namedStats = {};
        if (!s.cachedStats.namedStats.volume)
          s.cachedStats.namedStats.volume = {} as VolumeStats;
        s.cachedStats.namedStats.volume.value = valueML;
        s.cachedStats.namedStats.volume.unit = 'ml';
      }
    });
  });
}

function toSegmentationChangedPayload(
  sourcePayload: ToSegmPayload,
  locked: boolean
) {
  const measurementId =
    sourcePayload?.measurementId ||
    sourcePayload?.measurement?.measurementInstanceId;

  const label = sourcePayload?.config?.metadata?.segmentationLabel;

  // Pass through real segmentation objects without changes
  if (sourcePayload && sourcePayload.segments && sourcePayload.segmentationId) {
    return sourcePayload;
  }

  // Only build a mock for NA cases; avoid overwriting valid data otherwise
  const isNA = !!sourcePayload?.isNA;
  const isNE = !!sourcePayload?.isNE;
  const isNEA = isNA || isNE;
  if (!isNEA) {
    return null;
  }

  return {
    // keep segmentationId stable and, if possible, equal to measurement id
    segmentationId: measurementId ?? 'na-segmentation',
    // include measurement object for the form to pick measurementInstanceId
    measurementInstanceId: measurementId,
    label: label ?? null,
    cachedStats: {},
    segments: {
      1: {
        segmentIndex: 1,
        label: label ?? 'NA',
        locked,
        cachedStats: {},
        active: false,
      },
    },
    isNA,
    isNE,
  };
}

export function* scoringFormSegmentationChangedWorker(
  action: PayloadAction<unknown>
): Generator {
  const payload = action.payload;

  const data = yield* selectMeasurementsContextIfAvailable();
  if (!data) return;

  const { task, subTask } = data;

  const scoringCode = findScoringCode(subTask?.visits || task?.visits || []);

  if (scoringCode) {
    yield call(scoringFormSegmentationChangedAction, scoringCode, payload);
  }
}

export function* segmentationSavedWorker(
  action: PayloadAction<ToSegmPayload & { ok?: boolean }>
): Generator {
  const payload = toSegmentationChangedPayload(action.payload, true);
  if (payload) {
    yield* scoringFormSegmentationChangedWorker({
      type: Rpc.Events.Viewer.SEGMENTATION_CHANGED,
      payload: {
        ...payload,
        eventName: action.payload?.ok === false ? 'lockFailed' : 'lockSuccess',
      },
    });
  }
}

export function* segmentationMeasurementCreateWorker(
  payload: SegmentationServiceLockRequestParams & {
    clinicalTrialId: string;
    taskId: string;
  }
): Generator {
  const {
    seriesInstanceUid,
    referencedSeriesInstanceUID,
    measurementLabelId,
    instrumentId,
    config,
    data,
    clinicalTrialId,
    taskId,
  } = payload;

  if (!clinicalTrialId || !taskId) {
    return;
  }

  const inventory: ClinicalTrialInventoryType = yield select(
    clinicalTrialInventorySlice.selectors.selectClinicalTrialInventory
  );

  const scoringFormCode =
    inventory.scoringForms.find(form => form.id === config.scoringFormId)
      ?.code ?? '';

  const shouldConvertVolumeToMl =
    scoringFormCode !== ScoringFormsEnum.THIGH_FAT_FRACTION_SCORING_FORM;

  const isNEA = payload.isNA || payload.isNE;
  // Normalize volume to mL for outbound payload (idempotent)
  const normalizedData = isNEA
    ? {}
    : normalizeVolumeInPayloadToMl(data, shouldConvertVolumeToMl);

  const request: SegmentationMeasurementReqParams = {
    index: 0,
    instrumentStateJson: { annotation: {}, measurementState: {} },
    instrumentStateText: JSON.stringify({
      seriesInstanceUid,
      referencedSeriesInstanceUID,
    }),
    measurementName: config.metadata.segmentationLabel as string,
    measurementData: isNEA
      ? {
          data: {},
          isNA: payload.isNA,
          isNE: payload.isNE,
          comment: payload.comment,
          manufacturer: payload.manufacturer,
        }
      : {
          data: normalizedData,
          manufacturer: payload.manufacturer,
        },
    instrumentId,
    measurementLabelId,
    seriesInstanceUid,
  };

  return yield call(api.createMeasurement, clinicalTrialId, taskId, request);
}

export function* segmentationMeasurementUpdateWorker(
  payload: SegmentationServiceLockRequestParams & {
    clinicalTrialId: string;
    taskId: string;
  }
): Generator {
  const {
    measurementId,
    clinicalTrialId,
    taskId,
    data,
    seriesInstanceUid,
    referencedSeriesInstanceUID,
    config,
    measurementLabelId,
  } = payload;
  if (!measurementId || !clinicalTrialId || !taskId) {
    return;
  }

  const inventory: ClinicalTrialInventoryType = yield select(
    clinicalTrialInventorySlice.selectors.selectClinicalTrialInventory
  );

  const scoringFormCode =
    inventory.scoringForms.find(form => form.id === config.scoringFormId)
      ?.code ?? '';

  const shouldConvertVolumeToMl =
    scoringFormCode !== ScoringFormsEnum.THIGH_FAT_FRACTION_SCORING_FORM;

  // Normalize volume to mL for outbound payload (idempotent)
  const normalizedData = normalizeVolumeInPayloadToMl(
    data,
    shouldConvertVolumeToMl
  );

  const request = {
    index: 0,
    instrumentStateJson: {},
    instrumentStateText: JSON.stringify({
      seriesInstanceUid,
      referencedSeriesInstanceUID,
    }),
    measurementName: config.metadata.segmentationLabel as string,
    measurementData: {
      data: normalizedData,
      isNA: payload.isNA,
      isNE: payload.isNE,
      comment: payload.comment,
      manufacturer: payload.manufacturer,
    },
    measurementLabelId,
    seriesInstanceUid,
  } as MeasurementReqParams;

  yield call(
    api.updateMeasurement,
    measurementId,
    clinicalTrialId,
    taskId,
    request
  );
}

export function* segmentationMeasurementLockWorker(
  action: PayloadAction<SegmentationServiceLockRequestParams>
): Generator {
  const callId = action.payload?.__callId;
  try {
    const payload = action.payload;

    const dataLock = yield* selectMeasurementsContextIfAvailable();
    if (!dataLock) {
      throw new Error('Measurements context is not available for this role');
    }

    const { task, subTask } = dataLock;

    const clinicalTrialId: string =
      subTask?.clinicalTrialId || task?.clinicalTrialId || '';
    const taskId: string = subTask?.id || task?.id || '';

    // 1) If measurementId is provided, use it; otherwise create measurement first
    let measurementId = payload?.measurementId;

    if (!measurementId) {
      const created = yield* segmentationMeasurementCreateWorker({
        ...action.payload,
        clinicalTrialId,
        taskId,
      });
      measurementId = created?.data?.id;
    }

    if (!measurementId || !clinicalTrialId || !taskId) {
      throw new Error('Missing required identifiers for locking measurement');
    }

    // 2) Update measurement data before locking (payload.data)
    yield* segmentationMeasurementUpdateWorker({
      clinicalTrialId,
      taskId,
      ...payload,
    });

    // 3) Lock on backend
    yield call(api.lockMeasurement, measurementId, clinicalTrialId, taskId);

    resolveFormCall(callId, { measurementId });

    // Forward segmentation state to scoring form via orchestrator
    const forwardPayload = toSegmentationChangedPayload(
      { ...payload, measurementId },
      true
    );

    if (forwardPayload) {
      yield* scoringFormSegmentationChangedWorker({
        type: Rpc.Events.Viewer.SEGMENTATION_CHANGED,
        payload: { ...forwardPayload, eventName: 'lockSuccess' },
      });
    }
  } catch (error) {
    rejectFormCall(callId, error);
  }
}

export function* segmentationMeasurementUnlockWorker(
  action: PayloadAction<SegmentationUnlockParams>
): Generator {
  const callId = action.payload?.__callId;
  try {
    const { measurementId } = action.payload || {};

    const dataUnlock = yield* selectMeasurementsContextIfAvailable();
    if (!dataUnlock) {
      throw new Error('Measurements context is not available for this role');
    }

    const { task, subTask } = dataUnlock;

    const clinicalTrialId: string =
      subTask?.clinicalTrialId || task?.clinicalTrialId || '';
    const taskId: string = subTask?.id || task?.id || '';

    if (!measurementId || !clinicalTrialId || !taskId) {
      throw new Error('Missing required identifiers for unlocking measurement');
    }

    // Unlock on backend
    yield call(api.unlockMeasurement, measurementId, clinicalTrialId, taskId);

    // Delete measurement immediately for consistence state with form
    if (action.payload?.isNA || action.payload?.isNE) {
      yield call(api.deleteMeasurement, measurementId, clinicalTrialId, taskId);
    }

    resolveFormCall(callId, {});

    // Forward segmentation state with unlocked flag (for NA only)
    const forwardPayload = toSegmentationChangedPayload(
      {
        measurementId,
        config: action.payload?.config,
        isNA: action.payload?.isNA,
        isNE: action.payload?.isNE,
      },
      false
    );
    if (forwardPayload) {
      yield* scoringFormSegmentationChangedWorker({
        type: Rpc.Events.Viewer.SEGMENTATION_CHANGED,
        payload: { ...forwardPayload, eventName: 'unlockSuccess' },
      });
    }
  } catch (error) {
    rejectFormCall(callId, error);

    // On failure, for NA explicitly keep locked state if needed; don't send null
    const fallbackPayload = toSegmentationChangedPayload(
      {
        measurementId: action.payload?.measurementId,
        config: action.payload?.config,
        isNA: action.payload?.isNA,
        isNE: action.payload?.isNE,
      },
      true
    );
    if (fallbackPayload) {
      yield* scoringFormSegmentationChangedWorker({
        type: Rpc.Events.Viewer.SEGMENTATION_CHANGED,
        payload: { ...fallbackPayload, eventName: 'unlockFailed' },
      });
    }
  }
}

export function* segmentationMeasurementDeleteWorker(
  action: PayloadAction<SegmentationDeleteParams>
): Generator {
  const callId = action.payload?.__callId;
  try {
    const { measurementId } = action.payload || {};

    const dataDelete = yield* selectMeasurementsContextIfAvailable();
    if (!dataDelete) {
      throw new Error('Measurements context is not available for this role');
    }

    const { task, subTask } = dataDelete;

    const clinicalTrialId: string =
      subTask?.clinicalTrialId || task?.clinicalTrialId || '';
    const taskId: string = subTask?.id || task?.id || '';

    if (!measurementId || !clinicalTrialId || !taskId) {
      throw new Error('Missing required identifiers for deleting measurement');
    }

    // Delete on backend
    yield call(api.deleteMeasurement, measurementId, clinicalTrialId, taskId);

    resolveFormCall(callId, { measurementId });
  } catch (error) {
    rejectFormCall(callId, error);
  }
}

export function* viewerBusyChangedWorker(
  action: PayloadAction<{ reason: string; busy: boolean } | unknown>
): Generator {
  const payload = action.payload as
    | { reason?: string; busy?: boolean }
    | unknown;

  const data = yield* selectMeasurementsContextIfAvailable();
  if (!data) return;

  const { task, subTask } = data;

  const scoringCode = findScoringCode(subTask?.visits || task?.visits || []);

  if (scoringCode) {
    yield call(scoringFormViewerBusyChangedAction, scoringCode, payload);
  }
}
export function* selectedLabelsWorker(
  action: PayloadAction<{ label: string; value: string }[]>
) {
  const payload = action.payload;

  const data = yield* selectMeasurementsContextIfAvailable();
  if (!data) return;

  const { task, subTask } = data;

  const scoringCode = findScoringCode(subTask?.visits || task?.visits || []);

  if (scoringCode) {
    yield delay(3000);
    yield call(scoringFormSelectedLabelsAction, scoringCode, payload);
  }
}

export function* segmentationSaga(): Generator {
  yield takeLatest(
    segmentationSlice.actions.lockMeasurement.type,
    keepWatcherAlive(segmentationMeasurementLockWorker)
  );
  yield takeLatest(
    segmentationSlice.actions.unlockMeasurement.type,
    keepWatcherAlive(segmentationMeasurementUnlockWorker)
  );
  yield takeLatest(
    segmentationSlice.actions.deleteMeasurement.type,
    keepWatcherAlive(segmentationMeasurementDeleteWorker)
  );
  yield takeLatest(
    [
      Rpc.Events.Viewer.SEGMENTATION_SELECTED,
      Rpc.Events.Viewer.SEGMENTATION_CHANGED,
    ],
    keepWatcherAlive(
      scoringFormSegmentationChangedWorker,
      'Failed to apply segmentation change'
    )
  );
  yield takeLatest(
    Rpc.Events.Viewer.SEGMENTATION_SAVED,
    keepWatcherAlive(
      segmentationSavedWorker,
      'Failed to apply segmentation change'
    )
  );

  // NOTE: no toast — these fire on every viewer tick/selection, so a failing
  // forward would spam the user; the guard's console.error is enough.
  yield takeLatest(
    Rpc.Events.Viewer.SELECTED_LABELS,
    keepWatcherAlive(selectedLabelsWorker)
  );
  yield takeLatest(
    Rpc.Events.Viewer.BUSY_CHANGED,
    keepWatcherAlive(viewerBusyChangedWorker)
  );
}
